// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's guides and dimension chains as things Vælg picks and
 * edits (frontend-u0o.11): what lies under a click and what a box takes
 * (alongside the model's elements, `geometriSelect.ts`), the selection
 * bar's title for them, and what their grips do — a chain's line moves to
 * a new offset, its points move or go, a guide moves to the pointer or a
 * typed distance. World x/y, metres. Pure: no React, no DOM.
 */

import type { Annotations } from './cadAnnotations';
import { dimGeometry, dimOffset, guideLabel, onStorey, type DimChain, type Guide, type GuideLine, type GuidePoint } from './cadMeasure';
import { lengthInput, parseLength } from './geometriEdit';
import { clickSelect, inConvex, polyIn, type BoxMode } from './geometriSelect';
import type { XY } from './planWalls';

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const left = (d: XY): XY => [-d[1], d[0]];

function distToSegment(a: XY, b: XY, p: XY): number {
  const d = sub(b, a);
  const L2 = dot(d, d);
  const t = L2 > 0 ? Math.max(0, Math.min(1, dot(sub(p, a), d) / L2)) : 0;
  return dist(p, add(a, mul(d, t)));
}

/** A chain's drawn lines: the dimension line and every witness. */
function chainSegments(d: DimChain): [XY, XY][] {
  const g = dimGeometry(d);
  return [g.line, ...g.witness];
}

/**
 * The guides and chains under plan point `p` on storey `storeyId`, within
 * `tol` metres, in the order a click picks them: chains and guide points
 * (nearest first), then guide lines — they run across the whole plan, so
 * whatever else is there is the likelier aim.
 */
export function annotationsAt(a: Annotations, p: XY, tol: number, storeyId: number | null): string[] {
  const hits: { id: string; d: number; rank: number }[] = [];
  for (const d of onStorey(a.dims, storeyId)) {
    const near = Math.min(...chainSegments(d).map(([s, e]) => distToSegment(s, e, p)));
    if (near <= tol) hits.push({ id: d.id, d: near, rank: 0 });
  }
  for (const g of onStorey(a.guides, storeyId)) {
    if (g.kind === 'point') {
      const near = dist(g.p, p);
      if (near <= tol * 1.5) hits.push({ id: g.id, d: near, rank: 0 });
    } else {
      const near = Math.abs(dot(sub(p, g.a), left(g.dir)));
      if (near <= tol) hits.push({ id: g.id, d: near, rank: 1 });
    }
  }
  hits.sort((x, y) => x.rank - y.rank || x.d - y.d);
  return hits.map((h) => h.id);
}

/** Whether the infinite line through `a` along `dir` crosses the convex quad. */
function lineCrosses(quad: readonly XY[], a: XY, dir: XY): boolean {
  const n = left(dir);
  let lo = Infinity;
  let hi = -Infinity;
  for (const q of quad) {
    const s = dot(sub(q, a), n);
    lo = Math.min(lo, s);
    hi = Math.max(hi, s);
  }
  return lo <= 0 && hi >= 0;
}

/**
 * The guides and chains a box takes on storey `storeyId`: a window those
 * wholly inside — chains by their points and line, guide points — and never
 * a guide line, which has no end to be inside; a crossing whatever it
 * touches, but guide lines only when it caught nothing else (`others`: the
 * box took model elements) — one runs across every box drawn over the plan.
 */
export function annotationsInBox(a: Annotations, quad: readonly XY[], mode: BoxMode, storeyId: number | null, others: boolean): string[] {
  const out: string[] = [];
  for (const d of onStorey(a.dims, storeyId)) {
    const segs = chainSegments(d);
    const hit = mode === 'window' ? polyIn(quad, [...d.points, ...segs[0]], 'window', false) : segs.some((s) => polyIn(quad, s, 'crossing', false));
    if (hit) out.push(d.id);
  }
  const lines: string[] = [];
  for (const g of onStorey(a.guides, storeyId)) {
    if (g.kind === 'point') {
      if (inConvex(quad, g.p)) out.push(g.id);
    } else if (mode === 'crossing' && lineCrosses(quad, g.a, g.dir)) lines.push(g.id);
  }
  return out.length > 0 || others ? out : lines;
}

// ------------------------------------------------------------ selection --

/** The two halves of Geometri's selection: the model's elements (guids) and the guides / chains (ids). */
export interface PickSelection {
  elements: readonly string[];
  annotations: readonly string[];
}

export type PickHit = { kind: 'element' | 'annotation'; id: string };

/**
 * The selection after a click on `hit` (null: empty plan): a plain click
 * selects it alone, whichever kind (or clears both); Shift toggles it in
 * or out and keeps the rest of both.
 */
export function pickClick(current: PickSelection, hit: PickHit | null, additive: boolean): PickSelection {
  if (!additive) {
    if (hit === null) return { elements: [], annotations: [] };
    return hit.kind === 'element' ? { elements: [hit.id], annotations: [] } : { elements: [], annotations: [hit.id] };
  }
  if (hit === null) return { elements: [...current.elements], annotations: [...current.annotations] };
  return hit.kind === 'element'
    ? { elements: clickSelect(current.elements, hit.id, true), annotations: [...current.annotations] }
    : { elements: [...current.elements], annotations: clickSelect(current.annotations, hit.id, true) };
}

/**
 * What a click on the plan can pick, in order (Tab cycles them): chains
 * and guide points (thin, drawn over everything), then the openings and
 * walls (`elementsAt`), then guide lines — one along a wall's face must not
 * take the wall's clicks — then the rooms holding the point.
 */
export function pickCandidates(a: Annotations, annotationHits: readonly string[], elementHits: readonly string[], isSpace: (guid: string) => boolean): PickHit[] {
  const isLine = (id: string) => a.guides.some((g) => g.id === id && g.kind === 'line');
  const ann = (ids: readonly string[]): PickHit[] => ids.map((id) => ({ kind: 'annotation', id }));
  const el = (ids: readonly string[]): PickHit[] => ids.map((id) => ({ kind: 'element', id }));
  return [
    ...ann(annotationHits.filter((id) => !isLine(id))),
    ...el(elementHits.filter((g) => !isSpace(g))),
    ...ann(annotationHits.filter(isLine)),
    ...el(elementHits.filter(isSpace)),
  ];
}

/** "Målkæde 2 · 4,25 m", "Hjælpelinje 1", "Hjælpepunkt 3" (null: not there). */
export function annotationLabel(a: Annotations, id: string): string | null {
  const d = a.dims.find((x) => x.id === id);
  if (d) return `Målkæde ${a.dims.indexOf(d) + 1} · ${lengthInput(dimGeometry(d).total)} m`;
  const g = a.guides.find((x) => x.id === id);
  return g ? guideLabel(a.guides, g) : null;
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The selection bar's title for selected guides and chains: one by name, several counted. */
export function annotationTitle(a: Annotations, ids: readonly string[]): string {
  if (ids.length === 1) return annotationLabel(a, ids[0]) ?? '';
  return annotationCount(a, ids);
}

/** "1 målkæde · 2 hjælpelinjer": the short form beside selected elements. */
export function annotationCount(a: Annotations, ids: readonly string[]): string {
  const dims = ids.filter((id) => a.dims.some((d) => d.id === id)).length;
  const guides = ids.filter((id) => a.guides.some((g) => g.id === id)).length;
  return [dims > 0 ? count(dims, 'målkæde', 'målkæder') : null, guides > 0 ? count(guides, 'hjælpelinje', 'hjælpelinjer') : null].filter(Boolean).join(' · ');
}

/** Only the ids still there (a removed or undone one drops out of the selection). */
export function existingIds(a: Annotations, ids: readonly string[]): string[] {
  return ids.filter((id) => a.dims.some((d) => d.id === id) || a.guides.some((g) => g.id === id));
}

// ---------------------------------------------------------------- grips --

const typedOf = (typed: string | null): number | null => {
  if (!typed) return null;
  const v = parseLength(typed);
  return v !== null && v >= 0 ? v : null;
};

/** The chain with its line at the pointer's offset, or a typed one on the pointer's side. */
export function chainOffsetTo(d: DimChain, pointer: XY, typed: string | null): DimChain {
  const raw = dimOffset(d.points, d.dir, pointer);
  const t = typedOf(typed);
  return { ...d, offset: t !== null ? (raw < 0 ? -t : t) : raw };
}

/** The offset that keeps the dimension line where it stands with `first` as the chain's first point. */
const offsetFrom = (d: DimChain, first: XY) => d.offset - dot(sub(first, d.points[0]), left(d.dir));

/** The chain with point `i` at `p`; the dimension line stays where it stood. */
export function moveChainPoint(d: DimChain, i: number, p: XY): DimChain {
  const points = d.points.map((q, j) => (j === i ? ([p[0], p[1]] as XY) : q));
  return { ...d, points, offset: offsetFrom(d, points[0]) };
}

/** The chain without point `i` (the line staying put); null when fewer than two would be left. */
export function removeChainPoint(d: DimChain, i: number): DimChain | null {
  if (d.points.length <= 2 || i < 0 || i >= d.points.length) return null;
  const points = d.points.filter((_, j) => j !== i);
  return { ...d, points, offset: offsetFrom(d, points[0]) };
}

/** A guide line moved square to itself to the pointer, or a typed distance towards it; `moved`: signed, along its left normal. */
export function moveGuideLine(g: GuideLine, pointer: XY, typed: string | null): { guide: GuideLine; moved: number } {
  const n = left(g.dir);
  const raw = dot(sub(pointer, g.a), n);
  const t = typedOf(typed);
  const moved = t !== null ? (raw < 0 ? -t : t) : raw;
  return { guide: { ...g, a: add(g.a, mul(n, moved)) }, moved };
}

/** A guide point moved to the pointer, or a typed distance towards it; `moved`: how far. */
export function moveGuidePoint(g: GuidePoint, pointer: XY, typed: string | null): { guide: GuidePoint; moved: number } {
  const t = typedOf(typed);
  const v = sub(pointer, g.p);
  const L = Math.hypot(v[0], v[1]);
  if (t === null) return { guide: { ...g, p: [pointer[0], pointer[1]] }, moved: L };
  const dir: XY = L > 1e-9 ? [v[0] / L, v[1] / L] : [1, 0];
  return { guide: { ...g, p: add(g.p, mul(dir, t)) }, moved: t };
}

/** A guide line's grip: the foot of `near` (the view's middle) on it, so it is on screen. */
export function guideGripAt(g: GuideLine, near: XY): XY {
  return add(g.a, mul(g.dir, dot(sub(near, g.a), g.dir)));
}

export type { Guide };
